<template>
        <div class="sms-template">
            <h1 class="text-left">Select a WhatsApp Template</h1>

            <ValidationObserver ref="whatsAppTemplateForm" tag="form" v-on:submit.prevent="saveContent()">
                <div>
                    <ul>
                        <li>
                            <label for="template">WhatsApp Template</label>
                            <div>
                                <ValidationProvider rules="required" v-slot="{ errors }">
                                    <select v-if="automation" v-model="automation.whatsAppTemplateReference" id="template" v-on:change="showSelectedTemplate">
                                        <option v-bind:value="null">Select a Template</option>
                                        <option v-for="(template) of whatsAppTemplatesToShow" v-bind:key="template.reference" v-bind:value="template.reference">{{template.name}}</option>
                                    </select>
                                    <span class="form__error" v-if="errors">{{ errors[0] }}</span>
                                </ValidationProvider>
                            </div>
                        </li>

                        <li>
                            <div class="sms-template__preview">
                                <label for="preview">Preview</label>
                                <div class="sms-template__preview-note">
                                    <span class="">Variables in your message are replaced with sample data and highlighted in</span> <span class='sms-template__preview-variable'>blue</span>
                                </div>
                                <div id="preview" class="sms-template__preview-phone">

                                    <div class="sms-template__preview-phone-whatsapp">
                                        <PreviewTemplate v-if="selectedTemplate" :template="selectedTemplate" :variablesCopy="variablesCopy" :embedHeight="'300px'"></PreviewTemplate>
                                    </div>

                                </div>
                            </div>
                        </li>
                    </ul>


                    <div v-if="updating || error" class="infobar" v-bind:class="{'infobar-info' : updating, 'infobar-error' : error}">
                        {{infoMessage}}
                    </div>

                    <div class="row" v-show="!updating">
                        <div class="col-6 offset-3">
                            <div class="row">
                                <div class="col-6">
                                    <button class="btn btn-secondary" v-on:click.prevent="cancel">Cancel</button>
                                </div>
                                <div class="col-6">
                                    <button class="btn btn-primary" v-bind:aria-disabled="!changed" v-bind:class="{'btn-disabled' : !changed}">Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ValidationObserver>

            <confirm-dialog v-bind:continuePrimaryText="'Save'"
                            v-bind:message="confirmLoseChangesMessage"
                            v-bind:callbackPrimaryName="'save-sms-template'"
                            v-bind:callbackPrimaryReference="'save'"
                            v-if="showConfirmLoseChangesDialog"/>
        </div>
</template>

<script>
    import { ValidationObserver, ValidationProvider, /*extend*/ } from 'vee-validate'

    import { store } from '../store.js'
    import ConfirmDialog from '../components/ConfirmDialog.vue'
    import PreviewTemplate from "../components/whatsapp/PreviewTemplate.vue";

    import { variablesMixin } from '../mixins/VariablesMixin'
    import { stringsMixin } from "../mixins/StringsMixin"
    import { whatsAppTemplatesMixin } from "../mixins/WhatsAppTemplatesMixin"
    import { whatsAppTemplateMixin } from '../mixins/WhatsAppTemplateMixin'

    export default {
        name: 'SelectWhatsAppTemplate',
        components: {
            ValidationObserver, ValidationProvider, ConfirmDialog, PreviewTemplate
        },
        mixins: [ variablesMixin, whatsAppTemplatesMixin, stringsMixin, whatsAppTemplateMixin ],
        data() {
            return {
                reference: null,
                automation: null,
                requiredVariables: [],
                selectedTemplate: null,
                changed: false,
                cancelling: false,
                updating: false,
                error: false,
                infoMessage: "",
                confirmLoseChangesMessage: "You have un-saved changes to this SMS template. Would you like to save your work before leaving the page?",
                showConfirmLoseChangesDialog: false,
                navigatingToRoute: "",
                parentAutomation: null
            }
        },
        computed: {
            whatsAppTemplatesToShow: function() {
                if (!this.automation) {
                    return [];
                }
                if (this.automation.type === 'send-omni-channel-guest-form') {
                    return this.approvedFormInviteWhatsAppTemplates;
                }
                return this.approvedWhatsAppTemplates;
            },
            previewContentHTML: function() {
                if (!this.selectedTemplate) {
                    return "";
                }
                return this.previewTemplateContentHTML(this.selectedTemplate);
            },
            hasActions: function() {
                return this.selectedTemplate && this.doesTemplateHaveActions(this.selectedTemplate);
            },
            hasMedia: function() {
                return this.selectedTemplate && this.doesTemplateHaveMedia(this.selectedTemplate);
            },
            hasImageMedia: function() {
                return this.selectedTemplate && this.doesTemplateHaveImageMedia(this.selectedTemplate);
            },
            hasPDFMedia: function() {
                return this.selectedTemplate && this.doesTemplateHavePDFMedia(this.selectedTemplate);
            },

        },
        methods: {
            showSelectedTemplate: function() {
                if (this.automation.whatsAppTemplateReference) {
                    for (let template of this.whatsAppTemplates) {
                        if (template.reference === this.automation.whatsAppTemplateReference) {
                            this.selectedTemplate = template;
                            this.attachFilesAsMediaOnTemplate(this.selectedTemplate);
                            this.changed = true;
                            break;
                        }
                    }
                } else {
                    this.changed = false;
                    this.selectedTemplate = null;
                }
            },
            cancel: function() {
                console.log("cancel");
                this.cancelling = true;
                const route = { name: 'Automations' };
                if (this.parentAutomation) {
                    route.query = { automation: this.parentAutomation };
                }
                this.$router.push(route);
            },
            validateContent() {
                console.log("validating WhatsApp content");
                for (let requiredVariable of this.requiredVariables) {
                    let variable = '${' + requiredVariable.name + '}';
                    if (this.selectedTemplate.content.indexOf(variable) < 0) {
                        this.error = true;
                        this.infoMessage = "Your WhatsApp is missing the required variable '" + variable + "', which is required in order to send this WhatsApp to guests.";
                        return false;
                    }
                }
                return true;
            },
            saveContent: async function() {
                console.log("saving");
                if (!this.changed) {
                    return;
                }
                if (this.validateContent()) {
                    try {
                        const success = await this.$refs.whatsAppTemplateForm.validate();
                        console.log("Validated:" + success);
                        if (!success) {
                            return;
                        }
                        this.updating = true;
                        this.error = false;
                        this.infoMessage = "Saving WhatsApp template selection";

                        const body = {
                            reference: "" + this.automation.whatsAppTemplateReference,
                        };

                        await this.$http.put('/guest-management/v1/establishments/' + store.currentEstablishmentReference + '/automations/whatsapps/' + this.automation.reference + "/select-template", body);
                        console.log("WhatsApp template updated");
                        this.infoMessage = "WhatsApp template selected";
                        this.changed = false;
                        setTimeout(() => {
                            this.updating = false;
                        }, 2000);
                    } catch (error) {
                        this.updating = false;
                        this.error = true;
                        if (error.response) {
                            this.infoMessage = error.response.data.message;
                        } else {
                            this.infoMessage = "Sorry, we can't seem to reach our servers! Please try again";
                        }
                    }
                }
            }
        },
        mounted: async function() {
            // this.contentInput = this.$refs.content;

            await this.fetchWhatsAppTemplates();
            await this.fetchVariables();

            if (this.$route.query && this.$route.query.requiredVariables) {
                this.requiredVariables.splice(0, this.requiredVariables.length);
                const requiredVariable = {"name": this.$route.query.requiredVariables, "value": ""};
                this.requiredVariables.push(requiredVariable);
                this.variablesCopy.push(requiredVariable);
            }
            this.sortVariables();

            if (this.$route.query && this.$route.query.parentAutomation) {
                this.parentAutomation = this.$route.query.parentAutomation;
            }

            this.reference = this.$route.params.reference;
            this.$http.get('/guest-management/v1/establishments/' + store.currentEstablishmentReference + '/automations/' + this.reference).then(response => {
                this.automation = response.data;
                this.showSelectedTemplate();
                this.changed = false;
            }).catch(error => {
                console.log(error)
            });

            this.saveBeforeLeavingFn = async (action) => {
                if (action === "save") {
                    await this.saveContent();
                    this.$router.push({name: this.navigatingToRoute.name });
                    this.showConfirmLoseChangesDialog = false;
                } else {
                    this.showConfirmLoseChangesDialog = false;
                }
            };
            this.$root.$on('save-sms-template', this.saveBeforeLeavingFn);
        },
        beforeDestroy () {
            this.requiredVariables.splice(0, this.requiredVariables.length);
            this.$root.$off('save-sms-template', this.saveBeforeLeavingFn);
        },
        beforeRouteLeave (to, from, next) {
            if (this.changed && !this.cancelling) {
                this.navigatingToRoute = to;
                this.showConfirmLoseChangesDialog = true;
            } else {
                next();
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "../styles/layout/_forms.scss";
    @import "../styles/components/_question_answer.scss";
    @import "../styles/pages/_edit-sms.scss";
    @import "../styles/components/_message.scss";

    .sms-template__preview {
        margin-top: 0;
    }

    // Todo: ::v-deep Deprecated in Vue 3
    .sms-template__preview ::v-deep .sms-template__preview-variable {
        display: inline-block;
        border: 1px dotted $blue-dark;
        background-color: $blue-light;
        padding: 0 3px;
        color: $black;
    }
</style>

